import { HeatsSummary, HeatSeries } from "src/store/api/generatedApi";

export const HEAT_FILTER_FIELDS = ["eaf", "crew_id", "recipe_name"] as const;

export type HeatFilterField = (typeof HEAT_FILTER_FIELDS)[number];

export type HeatRowFilters = Record<HeatFilterField, string[]>;

export const NO_HEAT_ROW_FILTERS: HeatRowFilters = {
  eaf: [],
  crew_id: [],
  recipe_name: [],
};

export const isHeatFilterField = (field: string): field is HeatFilterField =>
  (HEAT_FILTER_FIELDS as readonly string[]).includes(field);

const distinctSorted = (values: (string | null | undefined)[]): string[] =>
  [
    ...new Set(
      values.filter(
        (value): value is string => value !== null && value !== undefined
      )
    ),
  ].sort((left, right) =>
    left.localeCompare(right, undefined, { numeric: true, sensitivity: "base" })
  );

export const heatFilterOptions = (series: HeatSeries[]): HeatRowFilters => ({
  eaf: distinctSorted(series.map((heat) => heat.eaf)),
  crew_id: distinctSorted(series.map((heat) => heat.crew_id)),
  recipe_name: distinctSorted(series.map((heat) => heat.recipe_name)),
});

const matchesSelection = (
  selected: string[],
  value: string | null | undefined
): boolean =>
  selected.length === 0 ||
  (value !== null && value !== undefined && selected.includes(value));

export const applyHeatFilters = (
  series: HeatSeries[],
  selection: HeatRowFilters
): HeatSeries[] =>
  series.filter(
    (heat) =>
      matchesSelection(selection.eaf, heat.eaf) &&
      matchesSelection(selection.crew_id, heat.crew_id) &&
      matchesSelection(selection.recipe_name, heat.recipe_name)
  );

export const isColumnFiltered = (selected: string[]): boolean =>
  selected.length > 0;

export const outputMassSummaryName = (heatsSummary: HeatsSummary): string =>
  heatsSummary.items.some((item) => item.summary_name === "yieldBillet")
    ? "billetsMass"
    : "tappedMass";

const summaryTotal = (series: HeatSeries[], summaryName: string): number =>
  series.reduce(
    (total, heat) =>
      total +
      (heat.summary_data.find((summary) => summary.name === summaryName)
        ?.value ?? 0),
    0
  );

export const weightedYieldPercent = (
  series: HeatSeries[],
  outputMassName: string
): number | null => {
  const chargedMass = summaryTotal(series, "chargedMass");
  if (chargedMass === 0) {
    return null;
  }
  return (summaryTotal(series, outputMassName) / chargedMass) * 100;
};
